// The responsive half of Box: turning `Responsive<T>` prop values into either
// breakpoint-prefixed Tailwind classes or per-breakpoint CSS variables.
//
// Two mechanisms, because Box has two kinds of prop:
//
//   TOKEN props (padding, columns, align, surface, …) hold a closed union, so
//   each value maps to a fixed utility and a breakpoint is the `md:` prefix.
//
//   VALUE props (width, top, zIndex, …) hold arbitrary numbers and strings, so
//   no class can exist for them. Today they land in inline `style`, and inline
//   styles carry no media queries — hence the CSS-variable path below.

import {
  BOX_BORDER_EDGE, BOX_BORDER_TOGGLE, BOX_DIRECTION, BOX_DISPLAY,
  BOX_ROUNDED, BOX_ROUNDED_SIDE, BREAKPOINTS,
  STYLE_VAR_PROPERTIES, UNITLESS_STYLE_VARS,
  type BorderEdge, type Breakpoint, type ColumnsToken, type DisplayToken,
  type FlexDirection, type Responsive, type RoundedSide, type RoundedToken,
  type StyleVarKey,
} from "./box-classes.ts";

/**
 * Tells a breakpoint object from a scalar.
 *
 * Sound only because no scalar Box prop value is a plain object: the sole
 * non-primitive values are arrays (`border`, `roundedSide`, `resize`), which
 * the `Array.isArray` arm excludes. See the `Responsive<T>` doc comment.
 */
export function isBreakpointObject<T>(
  value: Responsive<T> | undefined,
): value is Partial<Record<Breakpoint, T>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Prefixes EACH utility in a class string, not the string.
 *
 * Map values are frequently composite — `BOX_DIRECTION.column` is
 * `"flex flex-col"` and `BOX_FILL.true` is `"flex-1 w-full min-h-0 min-w-0"`.
 * Prefixing the whole string yields `"md:flex flex-col"`, where every utility
 * after the first silently escapes its breakpoint and applies everywhere.
 */
function prefix(breakpoint: Breakpoint, classes: string): string {
  return classes
    .split(" ")
    .filter(Boolean)
    .map((c) => `${breakpoint}:${c}`)
    .join(" ");
}

/** The value in force at `breakpoint`, following the mobile-first cascade down
 *  to the nearest specified breakpoint below it. A scalar applies everywhere. */
export function at<T>(
  value: Responsive<T> | undefined,
  breakpoint: Breakpoint,
): T | undefined {
  if (value == null) return undefined;
  if (!isBreakpointObject(value)) return value;
  for (let i = BREAKPOINTS.indexOf(breakpoint); i >= 0; i--) {
    const found = value[BREAKPOINTS[i] as Breakpoint];
    if (found != null) return found;
  }
  return undefined;
}

/** The general resolver: every other class helper here is a call to this one. */
export function resolveWith<T>(
  value: Responsive<T> | undefined,
  toClass: (value: T) => string | undefined,
): string | undefined {
  if (value == null) return undefined;
  if (!isBreakpointObject(value)) return toClass(value);

  const out: string[] = [];
  for (const breakpoint of BREAKPOINTS) {
    const scoped = value[breakpoint];
    if (scoped == null) continue;
    const classes = toClass(scoped);
    if (!classes) continue;
    out.push(breakpoint === "base" ? classes : prefix(breakpoint, classes));
  }
  return out.join(" ") || undefined;
}

/** A token prop against its class map. */
export function resolve<T extends string | number>(
  value: Responsive<T> | undefined,
  map: Record<T, string>,
): string | undefined {
  return resolveWith(value, (v) => map[v]);
}

/**
 * A boolean prop against its two-sided map.
 *
 * `scalarFalse` keeps each prop's existing scalar behaviour exactly: `shrink`
 * has always emitted `shrink-0` for a literal `false`, and `wrap`/`grow`/
 * `border` have always emitted nothing. Only the object form reaches the
 * `false` face for those three, so no existing call site changes output.
 */
export function resolveBoolean(
  value: Responsive<boolean> | undefined,
  map: Record<"true" | "false", string>,
  scalarFalse = false,
): string | undefined {
  if (value == null) return undefined;
  if (!isBreakpointObject(value)) {
    return value ? map.true : scalarFalse ? map.false : undefined;
  }
  return resolveWith(value, (v) => (v ? map.true : map.false));
}

/**
 * Narrows a responsive value to the breakpoints whose value passes `predicate`.
 *
 * `gap` is the reason this exists: it accepts a spacing token OR a raw number,
 * which take different mechanisms (class vs CSS variable), and an object form
 * may legitimately mix them — `gap={{ base: "sm", lg: 40 }}`.
 */
export function filterResponsive<T, U extends T>(
  value: Responsive<T> | undefined,
  predicate: (value: T) => value is U,
): Responsive<U> | undefined {
  if (value == null) return undefined;
  if (!isBreakpointObject(value)) return predicate(value) ? value : undefined;

  const out: Partial<Record<Breakpoint, U>> = {};
  let matched = false;
  for (const breakpoint of BREAKPOINTS) {
    const scoped = value[breakpoint];
    if (scoped != null && predicate(scoped)) {
      out[breakpoint] = scoped;
      matched = true;
    }
  }
  return matched ? out : undefined;
}

export const isToken = <T extends string>(v: T | number): v is T =>
  typeof v === "string";
export const isRawNumber = <T extends string>(v: T | number): v is number =>
  typeof v === "number";

/**
 * `border`, whose value is a boolean, one edge, or a list of edges.
 *
 * A scalar `false` emits nothing, exactly as today. Only the object form
 * reaches `border-0`, which it needs so a breakpoint can switch a border back
 * off after a lower one turned it on.
 */
export function resolveBorder(
  border: Responsive<boolean | BorderEdge | BorderEdge[]> | undefined,
): string | undefined {
  const responsive = isBreakpointObject(border);
  return resolveWith(border, (value) => {
    if (typeof value === "boolean") {
      if (value) return BOX_BORDER_TOGGLE.true;
      return responsive ? BOX_BORDER_TOGGLE.false : undefined;
    }
    const edges = Array.isArray(value) ? value : [value];
    return edges.map((edge) => BOX_BORDER_EDGE[edge]).join(" ");
  });
}

/**
 * `rounded` together with `roundedSide`, which pick one radius between them —
 * `roundedSide` names WHICH corners get the radius `rounded` names, so neither
 * can resolve without the other.
 */
export function resolveRounded(
  rounded: Responsive<RoundedToken> | undefined,
  roundedSide: Responsive<RoundedSide | RoundedSide[]> | undefined,
): string | undefined {
  if (rounded == null) return undefined;

  const sidesOf = (value: RoundedSide | RoundedSide[] | undefined) =>
    value == null ? [] : Array.isArray(value) ? value : [value];
  const classesFor = (radius: RoundedToken, sides: RoundedSide[]) =>
    sides.length === 0
      ? BOX_ROUNDED[radius]
      : sides.map((side) => BOX_ROUNDED_SIDE[side][radius]).join(" ");

  if (!isBreakpointObject(rounded) && !isBreakpointObject(roundedSide)) {
    return classesFor(rounded, sidesOf(roundedSide));
  }

  const out: string[] = [];
  let previous: string | undefined;
  for (const breakpoint of BREAKPOINTS) {
    const radius = at(rounded, breakpoint);
    if (radius == null) continue;
    const classes = classesFor(radius, sidesOf(at(roundedSide, breakpoint)));
    if (classes === previous) continue;
    previous = classes;
    out.push(breakpoint === "base" ? classes : prefix(breakpoint, classes));
  }
  return out.join(" ") || undefined;
}

// ---------------------------------------------------------------------------
// Display, direction and hidden
//
// These four props (`display`, `columns`, `direction`, `hidden`) decide ONE
// CSS property between them, so they cannot each resolve independently — the
// answer at a breakpoint depends on what the others say at that same
// breakpoint, inherited from below.
// ---------------------------------------------------------------------------

interface DisplayInput {
  display: Responsive<DisplayToken> | undefined;
  columns: Responsive<ColumnsToken> | undefined;
  direction: Responsive<FlexDirection> | undefined;
  /** Whether any flex-family prop appears at any breakpoint. */
  isFlex: boolean;
}

/**
 * The display classes in force at one breakpoint, as a single string.
 *
 * Mirrors the scalar precedence exactly: an explicit `display` (or `columns`,
 * which implies grid) wins and reduces `direction` to its axis alone;
 * otherwise `direction` brings its own `flex`, and any other flex-family prop
 * falls back to a column.
 */
function displayAt(input: DisplayInput, breakpoint: Breakpoint): string | undefined {
  const display = at(input.display, breakpoint);
  const columns = at(input.columns, breakpoint);
  const direction = at(input.direction, breakpoint);

  const explicit =
    columns != null && display == null
      ? BOX_DISPLAY.grid
      : display != null
        ? BOX_DISPLAY[display]
        : undefined;

  if (explicit) {
    // `direction` still contributes its axis under an explicit display:flex.
    const axis = direction ? BOX_DIRECTION[direction].split(" ")[1] : undefined;
    return axis ? `${explicit} ${axis}` : explicit;
  }
  if (direction) return BOX_DIRECTION[direction];
  if (input.isFlex) return BOX_DIRECTION.column;
  return undefined;
}

/** Emits a display class only where the resolved answer CHANGES, so a value
 *  that holds across breakpoints costs one class rather than six. */
export function resolveDisplay(input: DisplayInput): string | undefined {
  const out: string[] = [];
  let previous: string | undefined;
  for (const breakpoint of BREAKPOINTS) {
    const classes = displayAt(input, breakpoint);
    if (classes === previous) continue;
    previous = classes;
    if (!classes) continue;
    out.push(breakpoint === "base" ? classes : prefix(breakpoint, classes));
  }
  return out.join(" ") || undefined;
}

/**
 * `hidden`, whose false face is the Box's OWN display at that breakpoint.
 *
 * There is no single un-hiding class: the right answer is `block`, `flex` or
 * `grid` depending on what this Box is. A scalar keeps today's one-sided
 * behaviour — `hidden={false}` emits nothing — so only the object form pays
 * for the coupling.
 */
export function resolveHidden(
  hidden: Responsive<boolean> | undefined,
  display: DisplayInput,
): string | undefined {
  if (hidden == null) return undefined;
  if (!isBreakpointObject(hidden)) return hidden ? "hidden" : undefined;

  const out: string[] = [];
  let previous: string | undefined;
  for (const breakpoint of BREAKPOINTS) {
    const on = at(hidden, breakpoint);
    if (on == null) continue;
    const classes = on
      ? "hidden"
      : (displayAt(display, breakpoint) ?? BOX_DISPLAY.block);
    if (classes === previous) continue;
    previous = classes;
    out.push(breakpoint === "base" ? classes : prefix(breakpoint, classes));
  }
  return out.join(" ") || undefined;
}

// ---------------------------------------------------------------------------
// Mechanism B — CSS variables
//
// The matching media queries live in the GENERATED `src/box-responsive.css`.
// Both sides read `STYLE_VAR_PROPERTIES`, so a key cannot exist on one and not
// the other.
// ---------------------------------------------------------------------------

const STYLE_VAR_KEYS = Object.keys(STYLE_VAR_PROPERTIES) as StyleVarKey[];
const UNITLESS: ReadonlySet<StyleVarKey> = new Set(UNITLESS_STYLE_VARS);

function formatValue(key: StyleVarKey, value: number | string): string {
  return typeof value === "number" && !UNITLESS.has(key)
    ? `${value}px`
    : String(value);
}

export interface StyleVarResult {
  /** Custom properties to merge into the element's inline style. */
  vars: Record<string, string>;
  /** The `data-box-r` attribute value, or undefined when nothing is responsive. */
  attribute: string | undefined;
}

/**
 * Turns the responsive value props into custom properties.
 *
 * Only entries whose value is a breakpoint OBJECT are handled here — a scalar
 * `width={360}` keeps Box's existing inline-style path untouched, so none of
 * the existing call sites change behaviour.
 *
 * Values are filled forward: `{ base: 100, lg: 300 }` writes all six variables,
 * with `md` carrying 100 and `xl`/`2xl` carrying 300. The alternative — nested
 * `var(--a, var(--b, …))` fallback chains in the stylesheet — is far harder to
 * read when a value fails to appear.
 */
export function resolveStyleVars(
  entries: Partial<Record<StyleVarKey, Responsive<number | string> | undefined>>,
): StyleVarResult {
  const vars: Record<string, string> = {};
  const tokens: string[] = [];

  for (const key of STYLE_VAR_KEYS) {
    const value = entries[key];
    if (value == null || !isBreakpointObject(value)) continue;
    tokens.push(key);
    for (const breakpoint of BREAKPOINTS) {
      const scoped = at(value, breakpoint);
      if (scoped == null) continue;
      const name = breakpoint === "base" ? `--box-${key}` : `--box-${key}-${breakpoint}`;
      vars[name] = formatValue(key, scoped);
    }
  }

  return { vars, attribute: tokens.length ? tokens.join(" ") : undefined };
}
